import PropTypes from 'prop-types';

// material-ui
import {
  Box,
  IconButton,
  Stack,
  Typography
} from '@mui/material';

// project import
import { Capsule } from 'components/worten/modal-dialog-v2';

// icons
import { MdInfo, MdWarning, MdError, MdClose } from 'react-icons/md';

// store
import useNotificationsStore from './notificationsStore';

// api
import { useApiGeneric } from 'pages/content/api/api-generic';

// deep-link helper
import { useNotificationAction } from 'utils/notificationAction';

// date formatting
import { formatDistanceToNow } from 'date-fns';
import { es } from 'date-fns/locale';

// Map notification `tipo` to an accent color + icon
const TYPE_COLOR = {
  info: '#00bcd4',
  action_required: '#ff9800',
  critical: '#f44336'
};

const TYPE_ICON = {
  info: MdInfo,
  action_required: MdWarning,
  critical: MdError
};

// Single notification rendered as a capsule (ModalDialogV2 template).
const NotificationItem = ({ notification }) => {
  const dismiss = useNotificationsStore(state => state.dismiss);
  const { fetchAPI } = useApiGeneric();
  const { handleNotificationAction } = useNotificationAction();

  const accent = TYPE_COLOR[notification.tipo] || TYPE_COLOR.info;
  const TypeIcon = TYPE_ICON[notification.tipo] || MdInfo;

  // Format the date to show how long ago the notification was created
  const formatDate = (dateString) => {
    try {
      return formatDistanceToNow(new Date(dateString), {
        addSuffix: true,
        locale: es
      });
    } catch (error) {
      return 'fecha desconocida';
    }
  };

  // Click on the body: navigate to accion_ruta (if any) and dismiss
  const handleBodyClick = () => {
    if (notification.accionRuta) {
      handleNotificationAction(notification);
    }
    dismiss(notification.id, fetchAPI);
  };

  // "x" button: dismiss the single notification
  const handleDismiss = (event) => {
    event.stopPropagation();
    dismiss(notification.id, fetchAPI);
  };

  const titulo = notification.titulo || notification.mensaje;

  return (
    <Box
      onClick={handleBodyClick}
      sx={{ width: '100%', display: 'flex' }}
    >
      <Capsule
        sx={{
          cursor: notification.accionRuta ? 'pointer' : 'default',
          transition: 'box-shadow 0.15s ease',
          '&:hover': { boxShadow: '0 2px 8px rgba(0, 0, 0, 0.14)' }
        }}
        sxContent={{ px: 2, py: 1.5 }}
      >
        <Stack
          direction="row"
          spacing={1.5}
          sx={{
            justifyContent: 'space-between',
            alignItems: 'center',
            width: '100%'
          }}
        >
          {/* Type icon (accent color per notification tipo) */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              flexShrink: 0,
              color: accent,
              fontSize: '1.375rem',
              lineHeight: 1
            }}
          >
            <TypeIcon />
          </Box>

          <Stack
            direction="column"
            sx={{
              flexGrow: 1,
              overflow: 'hidden',
              minWidth: 0
            }}
          >
            <Stack direction="row" spacing={0.5} sx={{ alignItems: 'baseline', width: '100%', minWidth: 0 }}>
              <Typography variant="body2" lineHeight={1.2} sx={{ flexShrink: 0 }}>
                {titulo}
              </Typography>
              {notification.titulo && notification.mensaje && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  noWrap
                  sx={{ minWidth: 0, flexGrow: 1 }}
                >
                  {notification.mensaje.trim()}
                </Typography>
              )}
            </Stack>
            <Typography variant="caption" color="text.secondary">
              {formatDate(notification.fechaCreacion)}
            </Typography>
          </Stack>

          <IconButton
            size="small"
            sx={{
              flexShrink: 0,
              color: 'text.secondary',
              borderRadius: '8px',
              cursor: 'pointer',
              width: '24px',
              height: '24px',
              padding: 0,
              '&:hover': { color: 'primary.main' }
            }}
            onClick={handleDismiss}
            title="Descartar"
          >
            <MdClose size={16} />
          </IconButton>
        </Stack>
      </Capsule>
    </Box>
  );
};

NotificationItem.propTypes = {
  notification: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    destinatarioId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    tipo: PropTypes.string,
    titulo: PropTypes.string,
    mensaje: PropTypes.string,
    accionRuta: PropTypes.string,
    accionTarget: PropTypes.string,
    fechaCreacion: PropTypes.string
  }).isRequired
};

export default NotificationItem;
